
export interface IframeMessageData {
    type: string;
    method?: string;
    args?: any[];
    requestId?: string;
    path?: string;
}

// Resource limits (see PRODUCTION_READINESS_AUDIT.md F13)
const MAX_ARGS = 32;
const MAX_ARG_LENGTH = 2000;
const MAX_DEPTH = 4;
const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 20;
const MAX_ROWS = 500;
const MAX_MESSAGES_PER_WINDOW = 100;
const RATE_WINDOW_MS = 1000;

const ALLOWED_METHODS = new Set(["log", "error", "warn", "info", "debug", "clear"]);

export class ConsoleBridge {
    private consoleOutput: HTMLElement | null = null;
    private _rateWindowStart: number = 0;
    private _rateWindowCount: number = 0;
    private _droppedCount: number = 0;
    private _dropNoticeTimer: ReturnType<typeof setTimeout> | null = null;

    constructor() {}

    setConsoleOutputElement(element: HTMLElement) {
        this.consoleOutput = element;
    }

    getIframeScript(): string {
        return `
<script id="iframe-console-bridge">
(function() {
    'use strict';
    if (window.parent === window) return; // Don't run if not in an iframe or if it's top window

    var MAX_ARGS = ${MAX_ARGS};
    var MAX_ARG_LENGTH = ${MAX_ARG_LENGTH};
    var MAX_DEPTH = ${MAX_DEPTH};
    var MAX_ARRAY_ITEMS = ${MAX_ARRAY_ITEMS};
    var MAX_OBJECT_KEYS = ${MAX_OBJECT_KEYS};

    var originalConsole = {};
    var methods = ['log', 'error', 'warn', 'info', 'debug', 'clear'];
    methods.forEach(method => {
        originalConsole[method] = console[method] ? console[method].bind(console) : () => {};
    });

    function truncate(value) {
        return value.length > MAX_ARG_LENGTH ? value.slice(0, MAX_ARG_LENGTH) + '\\u2026[truncated]' : value;
    }

    function serializeValue(value, depth, seen) {
        if (value === null) return 'null';
        if (depth > MAX_DEPTH) return '[Max Depth]';
        var t = typeof value;
        if (t === 'string') return truncate(value);
        if (t === 'number' || t === 'boolean' || t === 'undefined' || t === 'bigint') return String(value);
        if (t === 'function') return '[Function' + (value.name ? ': ' + value.name : '') + ']';
        if (t === 'symbol') return value.toString();
        if (value instanceof Error) return truncate('Error: ' + value.message + (value.stack ? '\\nStack: ' + value.stack : ''));
        if (typeof HTMLElement !== 'undefined' && value instanceof HTMLElement) {
            var attrs = Array.from(value.attributes).slice(0, MAX_OBJECT_KEYS).map(attr => attr.name + '="' + truncate(attr.value) + '"').join(' ');
            return truncate('<' + value.tagName.toLowerCase() + (attrs ? ' ' + attrs : '') + '>...');
        }
        if (seen.has(value)) return '[Circular Reference]';
        seen.add(value);
        try {
            if (Array.isArray(value)) {
                var items = [];
                var count = Math.min(value.length, MAX_ARRAY_ITEMS);
                for (var i = 0; i < count; i++) items.push(serializeValue(value[i], depth + 1, seen));
                if (value.length > count) items.push('\\u2026 ' + (value.length - count) + ' more');
                return truncate('[' + items.join(', ') + ']');
            }
            var keys = Object.keys(value).slice(0, MAX_OBJECT_KEYS);
            var entries = keys.map(function(key) {
                var display = key;
                try { display = key + ': ' + serializeValue(value[key], depth + 1, seen); } catch (e) { display = key + ': [Unserializable]'; }
                return display;
            });
            if (Object.keys(value).length > keys.length) entries.push('\\u2026 more properties');
            return truncate('{ ' + entries.join(', ') + ' }');
        } catch (e) {
            return '[Unserializable Object]';
        } finally {
            seen.delete(value);
        }
    }

    function formatArgsForPostMessage(args) {
        return Array.from(args).slice(0, MAX_ARGS).map(function(arg) {
            try { return serializeValue(arg, 0, new Set()); }
            catch (e) { return '[Unserializable Object]'; }
        });
    }

    methods.forEach(methodName => {
        if (methodName === 'clear') {
            console.clear = function() {
                originalConsole.clear();
                try { window.parent.postMessage({ type: 'iframe-console', method: 'clear' }, '*'); }
                catch(e) { originalConsole.error('SWO Console bridge error (clear):', e); }
            };
        } else {
            console[methodName] = function(...args) {
                originalConsole[methodName](...args);
                try { window.parent.postMessage({ type: 'iframe-console', method: methodName, args: formatArgsForPostMessage(args) }, '*'); }
                catch(e) { originalConsole.error('SWO Console bridge error ('+methodName+'):', e); }
            };
        }
    });

    window.addEventListener('error', function(event) {
        var errorArgs = ['Unhandled error: ' + event.message, 'at ' + (event.filename || 'unknown') + ':' + (event.lineno || 0) + ':' + (event.colno || 0)];
        originalConsole.error(...errorArgs);
        try { window.parent.postMessage({ type: 'iframe-console', method: 'error', args: errorArgs }, '*'); }
        catch(e) { originalConsole.error('SWO Console bridge error (onerror):', e); }
    });

    window.addEventListener('unhandledrejection', function(event) {
        var reason = event.reason instanceof Error ? event.reason.message + '\\n' + event.reason.stack : String(event.reason);
        var rejectionArgs = ['Unhandled promise rejection:', truncate(reason)];
        originalConsole.warn(...rejectionArgs);
        try { window.parent.postMessage({ type: 'iframe-console', method: 'warn', args: rejectionArgs }, '*'); }
        catch(e) { originalConsole.error('SWO Console bridge error (unhandledrejection):', e); }
    });

    // Notify parent that bridge is ready
    setTimeout(() => {
        try {
            window.parent.postMessage({ type: 'iframe-ready' }, '*');
            originalConsole.log("SWO Console bridge initialized in iframe.");
        } catch(e) { originalConsole.error('SWO Console bridge error (ready):', e); }
    }, 0);
})();
<\/script>
`;
    }

    // Accepts unknown input: preview code and third-party scripts can post
    // arbitrary messages to the host window (F12). Invalid envelopes are
    // silently dropped instead of throwing.
    handleMessage(data: unknown) {
        if (!this.consoleOutput) return;
        if (!data || typeof data !== "object") return;

        const msg = data as Record<string, unknown>;
        if (typeof msg.type !== "string") return;

        if (msg.type === "iframe-ready") {
            if (!this._acquireRateSlot()) return;
            this.logSpecialMessage("Console connected.", "swo-console-connected");
            return;
        }

        if (msg.type !== "iframe-console") return;

        const method = msg.method;
        if (typeof method !== "string" || !ALLOWED_METHODS.has(method)) return;

        if (method === "clear") {
            if (!this._acquireRateSlot()) return;
            this.clear(false); // false = cleared by iframe
            this.logSpecialMessage("Console cleared by iframe.", "swo-console-cleared");
            return;
        }

        let args: string[] = [];
        if (msg.args !== undefined && msg.args !== null) {
            if (!Array.isArray(msg.args)) return;
            const boundedArgs = msg.args.slice(0, MAX_ARGS);
            if (boundedArgs.some((arg) => typeof arg !== "string")) return;
            args = (boundedArgs as string[]).map((arg) =>
                arg.length > MAX_ARG_LENGTH ? arg.slice(0, MAX_ARG_LENGTH) + "\u2026[truncated]" : arg
            );
        }

        if (!this._acquireRateSlot()) return;

        this._appendMessageRow(method, args);
    }

    // Token-window rate limiting (F13). Messages beyond the per-window budget
    // are counted and reported once the window resets.
    private _acquireRateSlot(): boolean {
        const now = Date.now();
        if (now - this._rateWindowStart >= RATE_WINDOW_MS) {
            this._rateWindowStart = now;
            this._rateWindowCount = 0;
        }
        if (this._rateWindowCount >= MAX_MESSAGES_PER_WINDOW) {
            this._droppedCount++;
            if (!this._dropNoticeTimer) {
                this._dropNoticeTimer = setTimeout(() => {
                    this._dropNoticeTimer = null;
                    if (this._droppedCount > 0) {
                        const dropped = this._droppedCount;
                        this._droppedCount = 0;
                        this.logSpecialMessage(
                            `SWO: console rate limit reached \u2014 ${dropped} message(s) dropped.`,
                            "swo-console-cleared"
                        );
                    }
                }, RATE_WINDOW_MS);
            }
            return false;
        }
        this._rateWindowCount++;
        return true;
    }

    private _appendMessageRow(method: string, args: string[]) {
        if (!this.consoleOutput) return;

        const messageLine = document.createElement("div");
        messageLine.classList.add("swo-console-message-line");

        let methodClass = "swo-console-log",
            methodIcon = "➡️"; // Default for log

        switch (method) {
            case "error":
            methodClass = "swo-console-error";
            methodIcon = "❌";
            break;
            case "warn":
            methodClass = "swo-console-warn";
            methodIcon = "⚠️";
            break;
            case "info":
            methodClass = "swo-console-info";
            methodIcon = "ℹ️";
            break;
            case "debug":
            methodClass = "swo-console-debug";
            methodIcon = "🐞";
            break;
        }
        messageLine.classList.add(methodClass);

        const iconSpan = document.createElement("span");
        iconSpan.className = "swo-console-message-icon";
        iconSpan.textContent = methodIcon;
        messageLine.appendChild(iconSpan);

        const contentWrapper = document.createElement("div");
        contentWrapper.className = "swo-console-message-content-wrapper";

        if (args.length > 0) {
            const messageContent = document.createElement("pre");
            messageContent.className = "swo-console-message-content";
            messageContent.textContent = args.join(" ");
            contentWrapper.appendChild(messageContent);
        } else {
            const emptyMsg = document.createElement("span");
            emptyMsg.textContent =
            method === "log"
                ? "(empty log)"
                : `(${method} with no arguments)`;
            emptyMsg.style.opacity = "0.7";
            emptyMsg.style.fontStyle = "italic";
            contentWrapper.appendChild(emptyMsg);
        }
        messageLine.appendChild(contentWrapper);

        const output = this.consoleOutput;
        output.appendChild(messageLine);

        // Bound the number of retained rows (F13): drop the oldest lines.
        this._trimRows();

        output.scrollTop = output.scrollHeight;
    }

    clear(byEditor: boolean) {
        if (this._dropNoticeTimer) {
            clearTimeout(this._dropNoticeTimer);
            this._dropNoticeTimer = null;
        }
        this._droppedCount = 0;
        if (!this.consoleOutput) return;

        this.consoleOutput.innerHTML = "";
        if (byEditor) {
          this.logSpecialMessage(
            "Console cleared by editor.",
            "swo-console-cleared"
          );
        }
    }

    logSpecialMessage(text: string, className: string) {
        if (!this.consoleOutput) return;
        const msgDiv = document.createElement("div");
        msgDiv.className = `swo-console-special-message ${className}`;
        msgDiv.textContent = text;
        this.consoleOutput.appendChild(msgDiv);
        this._trimRows();
        this.consoleOutput.scrollTop = this.consoleOutput.scrollHeight;
    }

    private _trimRows() {
        if (!this.consoleOutput) return;
        while (this.consoleOutput.childElementCount > MAX_ROWS) {
            this.consoleOutput.firstElementChild?.remove();
        }
    }

    destroy() {
        if (this._dropNoticeTimer) {
            clearTimeout(this._dropNoticeTimer);
            this._dropNoticeTimer = null;
        }
        this._droppedCount = 0;
        this._rateWindowCount = 0;
        this.consoleOutput = null;
    }
}
